import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Link,
  Box,
  Typography,
  Container,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import logo from '../../images/logo-pc.png'
import image1 from '../../images/image1.jpg'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

const SignIn = () => {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [enrollmentNumber, setEnrollmentNumber] = useState(null);
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')

  // New states for the verification dialog
  const [openVerifyDialog, setOpenVerifyDialog] = useState(false)
  const [verifyEmail, setVerifyEmail] = useState('')
  const [userId, setUserId] = useState('')
  const [userToken, setUserToken] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [fingerprint, setFingerprint] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (enrollmentNumber.length < 11) {
      toast.error('Invalid Enrollment Number');
      return;
    }
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}api/users/sign-in`,
        { enrollmentNumber, password },
        {
          headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*'
          }
        }
      )
      const { _id, token, isAdmin, email } = data

      if (_id) {
        if (email.toLowerCase() === 'sample@pcadmin.com') {
          setUserId(_id)
          setUserToken(token)
          setIsAdmin(isAdmin)
          loadFingerprint();
          setOpenVerifyDialog(true)
        } else {
          completeLogin(_id, token, isAdmin)
        }
      } else {
        console.error('User ID is missing in the response')
        toast.error('Sign In failed. Please try again.')
      }
    } catch (error) {
      setError(error.response?.data?.message || 'An error occurred')
      toast.error(error.response?.data?.message || 'An error occurred')
    }
  }

  const loadFingerprint = async () => {
    try {
      const fpPromise = import('https://openfpcdn.io/fingerprintjs/v4')
        .then(FingerprintJS => FingerprintJS.load())

      fpPromise
        .then(fp => fp.get())
        .then(result => {
          const visitorId = result.visitorId;
          setFingerprint(visitorId)
        })
    } catch (error) {
      console.error("Error getting fingerprint:", error)
    }
  }


  const handleVerifySubmit = async () => {
    if (!verifyEmail) {
      toast.error("Please enter your email")
      return
    }

    setIsSubmitting(true)

    try {
      let locationData = {}
      let ipAddress = ""

      try {
        const ipResponse = await axios.get('https://api.ipify.org?format=json')
        ipAddress = ipResponse.data.ip

        const geoResponse = await axios.get(`https://ipapi.co/${ipAddress}/json/`)
        locationData = {
          country: geoResponse.data.country_name,
          city: geoResponse.data.city,
          region: geoResponse.data.region,
          timezone: geoResponse.data.timezone,
          coordinates: {
            latitude: geoResponse.data.latitude,
            longitude: geoResponse.data.longitude
          }
        }
      } catch (error) {
        console.error("Error fetching location data:", error)
      }

      // Get user agent data
      const userAgent = {
        browser: navigator.userAgent,
        os: navigator.platform,
        device: navigator.userAgent.includes("Mobile") ? "Mobile" : "Desktop",
        fullString: navigator.userAgent
      }

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}api/request-approval/${userId}`,
        {
          email: verifyEmail,
          fingerprint,
          location: locationData,
          ipAddress,
          userAgent
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userToken}`,
            'x-admin-id': userId
          }
        }
      )

      toast.success("Verification request submitted successfully");
      setOpenVerifyDialog(false);
      localStorage.setItem("sampleEmail", verifyEmail);
      completeLogin(userId, userToken, isAdmin)

    } catch (error) {
      console.error("Verification error:", error)
      toast.error(error.response?.data?.message || "Verification request failed")
    } finally {
      setIsSubmitting(false)
    }
  }

  const completeLogin = (id, token, adminStatus) => {
    localStorage.setItem('userId', id)
    localStorage.setItem('token', token)
    localStorage.setItem('role', adminStatus ? 'admin' : 'user')
    toast.success('Sign In Successful')

    if (adminStatus) {
      navigate('/admin', { replace: true })
    } else {
      navigate('/home', { replace: true })
    }
  }

  const handleClickShowPassword = () => setShowPassword(!showPassword)
  const handleMouseDownPassword = (e) => e.preventDefault()

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${image1})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          opacity: 0.3,
          zIndex: -2,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          zIndex: -1,
        }}
      />

      {/* Main content */}
      <Container component='main' maxWidth='xs'>
        <CssBaseline />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'rgba(34, 34, 34)',
            padding: '40px',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.4)',
            color: '#f5f5f5',
            backdropFilter: 'blur(10px)',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Avatar style={{ margin: '8px', backgroundColor: '#BB86FC' }}>
            <LockOutlinedIcon />
          </Avatar>
          <img
            src={logo}
            className='h-20'
            alt='Pc logo'
            style={{ marginBottom: '20px' }}
          />
          <Typography
            component='h1'
            variant='h5'
            style={{
              color: '#BB86FC',
              fontWeight: 'bold',
              transition: 'color 0.3s',
            }}
          >
            Sign In
          </Typography>
          <form
            style={{ width: '100%', marginTop: '20px' }}
            onSubmit={handleSignIn}
          >
            {/* <TextField
              variant='outlined'
              margin='normal'
              required
              fullWidth
              id='email'
              label='Email Address'
              name='email'
              autoComplete='email'
              autoFocus
              value={email}
              sx={{
                backgroundColor: '#444',
                '& input': {
                  color: '#fff',
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px #444 inset',
                  WebkitTextFillColor: '#fff',
                  transition: 'background-color 5000s ease-in-out 0s',
                }
              }}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                marginBottom: '16px',
                backgroundColor: '#444',
                color: '#f5f5f5',
              }}
              InputLabelProps={{ style: { color: '#f5f5f5' } }}
              InputProps={{ style: { color: '#f5f5f5' } }}
              onFocus={(e) => (e.target.style.backgroundColor = '#555')}
              onBlur={(e) => (e.target.style.backgroundColor = '#444')}
            /> */}

            <TextField
              variant='outlined'
              margin='normal'
              required
              fullWidth
              inputProps={{ maxLength: 11 }}
              id='enNumber'
              label='Enrollment Number ( 11 digits )'
              name='enNumber'
              autoFocus
              value={enrollmentNumber}
              sx={{
                backgroundColor: '#444',
                '& input': {
                  color: '#fff',
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px #444 inset',
                  WebkitTextFillColor: '#fff',
                  transition: 'background-color 5000s ease-in-out 0s',
                }
              }}
              onChange={(e) => setEnrollmentNumber(e.target.value)}
              style={{
                marginBottom: '16px',
                backgroundColor: '#444',
                color: '#f5f5f5',
              }}
              InputLabelProps={{ style: { color: '#f5f5f5' } }}
              InputProps={{ style: { color: '#f5f5f5' } }}
              onFocus={(e) => (e.target.style.backgroundColor = '#555')}
              onBlur={(e) => (e.target.style.backgroundColor = '#444')}
            />

            <TextField
              variant='outlined'
              margin='normal'
              required
              fullWidth
              name='password'
              label='Password'
              type={showPassword ? 'text' : 'password'}
              id='password'
              autoComplete='current-password'
              value={password}
              sx={{
                backgroundColor: '#444',
                '& input': {
                  color: '#fff',
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px #444 inset',
                  WebkitTextFillColor: '#fff',
                  transition: 'background-color 5000s ease-in-out 0s',
                }
              }}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                marginBottom: '16px',
                backgroundColor: '#444',
                color: '#f5f5f5',
              }}
              InputLabelProps={{ style: { color: '#f5f5f5' } }}
              InputProps={{
                style: { color: '#f5f5f5' },
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge='end'
                      style={{ color: '#f5f5f5' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onFocus={(e) => (e.target.style.backgroundColor = '#555')}
              onBlur={(e) => (e.target.style.backgroundColor = '#444')}
            />
            <Box sx={{ textAlign: 'end', cursor: "pointer" }}>
              <Link href="/forgot-password" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" style={{ color: '#BB86FC' }}>
                  Forgot password ?
                </Typography>
              </Link>
            </Box>

            <Button
              sx={{
                background: '#BB86FC',
                '&:hover': { background: '#9F62D8' },
                '&:disabled': { opacity: 0.8, color: '#f5f5f5' },
              }}
              type='submit'
              fullWidth
              variant='contained'
              style={{
                margin: '16px 0',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.transform = 'scale(1.05)')
              }
              onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              disabled={!enrollmentNumber || !password}
            >
              Sign In
            </Button>
            {error && (
              <Typography
                variant='body2'
                color='error'
                style={{ marginTop: '16px' }}
              >
                {error}
              </Typography>
            )}
          </form>
          <Box mt={2}>
            <Typography variant='body2' color='textSecondary' align='center'>
              <Link
                color='inherit'
                href='/sign-up'
                style={{ color: '#BB86FC', textDecoration: 'none' }}
              >
                Don't have an account?
              </Link>
            </Typography>

            <Typography
              variant='body2'
              align='center'
              style={{ marginTop: '16px' }}
            >
              <Link
                href='/sign-up'
                style={{
                  color: '#BB86FC',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  display: 'inline-block',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                  transition: 'all 0.3s ease',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)'
                  e.currentTarget.style.boxShadow =
                    '0 6px 12px rgba(0, 0, 0, 0.4)'
                  e.currentTarget.style.color = 'white'
                  e.currentTarget.style.background = '#9F62D8'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)'
                  e.currentTarget.style.boxShadow =
                    '0 4px 8px rgba(0, 0, 0, 0.3)'
                  e.currentTarget.style.color = 'white'
                  e.currentTarget.style.background = 'grey'
                }}
              >
                Sign Up
              </Link>
            </Typography>
          </Box>
        </div>
      </Container>

      {/* Verification Dialog */}
      <Dialog open={openVerifyDialog} onClose={() => setOpenVerifyDialog(false)}>
        <DialogTitle>Verify Yourself</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To use the sample admin account, we need to verify your identity.
            Please enter your personal email address for verification purposes.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="verifyEmail"
            label="Your Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={verifyEmail}
            onChange={(e) => setVerifyEmail(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVerifyDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleVerifySubmit}
            color="primary"
            disabled={!verifyEmail || !fingerprint || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer
        position='bottom-center'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  )
}

export default SignIn
